<template>
  <div class="position-relative">
    <v-card class="mb-4">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <span>{{ $t('burnup.title') }}</span>
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                icon="mdi-information"
                size="small"
                class="ms-1"
              ></v-icon>
            </template>
            <span>{{ $t('burnup.tooltip') }}</span>
          </v-tooltip>
        </div>
        <div class="d-flex align-center">
          <v-btn-toggle v-model="groupBy" variant="outlined" mandatory class="me-2">
            <v-btn value="day" :disabled="isLoading">{{ $t('burnup.day') }}</v-btn>
            <v-btn value="week" :disabled="isLoading">{{ $t('burnup.week') }}</v-btn>
            <v-btn value="month" :disabled="isLoading">{{ $t('burnup.month') }}</v-btn>
          </v-btn-toggle>
          <v-btn
            icon
            elevation="0"
            text
            @click="resetZoom"
            class="me-2"
            :disabled="!isZoomed || isLoading"
          >
            <v-icon>mdi-refresh</v-icon>
            <v-tooltip activator="parent" location="bottom">
              <span>{{ $t('burnup.resetZoom') }}</span>
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="buildMode === 'webapp'"
            icon
            elevation="0"
            text
            @click="downloadChart"
            :disabled="isLoading"
          >
            <v-icon>mdi-download</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text style="min-height: 400px" class="position-relative">
        <v-chart class="chart" ref="chartRef" :option="chartOption" @zr:click="handleChartClick" />
        
        <!-- Loading overlay -->
        <div v-if="isLoading" class="chart-loading-overlay">
          <v-progress-circular
            indeterminate
            color="primary"
            size="60"
          ></v-progress-circular>
          <div class="mt-3 text-center">{{ $t('loading') }}</div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineProps, shallowRef } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import { 
  TooltipComponent, 
  LegendComponent, 
  GridComponent, 
  DataZoomComponent,
  ToolboxComponent 
} from "echarts/components";
import VChart from "vue-echarts";
import { downloadChartAsPNG } from '@/utils/chartUtils';

use([
  CanvasRenderer, 
  LineChart, 
  TooltipComponent, 
  LegendComponent, 
  GridComponent,
  DataZoomComponent,
  ToolboxComponent
]);

const props = defineProps({
  filterStatusHistoryContext: {
    type: Object,
    required: true
  },
  dataLoaded: {
    type:Boolean,
    required:true,
  }
});

const store = useStore();
const { t } = useI18n();
const conn = computed(() => store.getters.getDBConnection);

const buildMode = computed(() => store.state.buildMode);
const statusTemplatesObj = computed(() => store.getters.getStatusTemplatesObj);
const chartColor = computed(() => store.getters.chartFontColor);

const chartRef = ref(null);
const chartOption = shallowRef({});
const groupBy = ref('month');
const chartData = ref({
  timeData: [],
  seriesData: [],
  rawData: new Map(),
  totalData: []
});

// Zoom related state
const isZoomed = ref(false);
const zoomStart = ref(0);
const zoomEnd = ref(100);

// Loading state
const isLoading = ref(false);

// Tooltip cache and loading state
const tooltipCache = ref(new Map());
const tooltipLoading = ref(false);
const tooltipDebounceTimer = ref(null);

const currentTooltipTimePoint = ref(null);
const tooltipState = ref({
  loading: false,
  data: null,
  timePoint: null
});

// Generic query execution function
const executeAndProcessQuery = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray();
};

const executeAndProcessTooltipQuery = async (query) => {
  const result = await conn.value.query(query);
  return result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      count: Number(jsonRow.count),
      sort: Number(jsonRow.sort)
    };
  });
}


 const generateBurnupQuery = (groupBy = "day") => {
  /* --- literals that vary with the caller ------------------------ */
  const unit =
        groupBy === "month" ? "month" :
        groupBy === "week"  ? "week"  : "day";

  const fmt  =
        groupBy === "month" ? "%Y-%m"   :
        groupBy === "week"  ? "%Y-W%V"  : "%Y-%m-%d";

  const interval =
        groupBy === "month" ? "INTERVAL 1 MONTH" :
        groupBy === "week"  ? "INTERVAL 1 WEEK"  : "INTERVAL 1 DAY";

  /* --- full SQL --------------------------------------------------- */
  return `
WITH
/* 1️⃣  +1 / −1 “delta” rows for each status period ------------------ */
deltas AS (
  SELECT subItemId,
         statusId,
         DATE_TRUNC('${unit}', validFrom)                                  AS bucket,
         +1 AS delta
    FROM filteredStatusHistory
  UNION ALL
  SELECT subItemId,
         statusId,
         DATE_TRUNC('${unit}',
                    COALESCE(validTo, CAST('9999-12-31' AS TIMESTAMP)))     AS bucket,
         -1 AS delta
    FROM filteredStatusHistory
),

/* 2️⃣  aggregate deltas per bucket × colour ------------------------- */
bucket_deltas AS (
  SELECT bucket,
         st.color,
         SUM(delta) AS delta
    FROM deltas d
         JOIN status_templates st ON d.statusId = st.id
   GROUP BY bucket, st.color
),

/* 3️⃣  canonical sort order per colour ------------------------------ */
color_sort_map AS (
  SELECT color, MIN(sort) AS standard_sort
    FROM status_templates
   GROUP BY color
),

/* 4️⃣  calendar buckets from first item to “now” -------------------- */
all_buckets AS (
  SELECT DATE_TRUNC('${unit}', gs) AS bucket                 -- ⭑ use alias gs
    FROM GENERATE_SERIES(
           (SELECT DATE_TRUNC('${unit}', MIN(createdDate)) FROM items),
           (SELECT DATE_TRUNC('${unit}', CAST(CURRENT_TIMESTAMP AS TIMESTAMP))),
           ${interval}
         ) AS series(gs)                                      -- ⭑ alias column
),

/* 5️⃣  cross product: every bucket × every colour ------------------- */
all_buckets_and_colors AS (
  SELECT b.bucket,
         c.color,
         c.standard_sort
    FROM all_buckets b
    CROSS JOIN color_sort_map c
),

/* 6️⃣  add real deltas and accumulate running sum ------------------ */
running AS (
  SELECT  abc.bucket,
          abc.color,
          abc.standard_sort,
          COALESCE(bd.delta, 0)                                           AS delta,
          SUM(COALESCE(bd.delta, 0))
              OVER (PARTITION BY abc.color ORDER BY abc.bucket)           AS cumulative_count
    FROM  all_buckets_and_colors abc
          LEFT JOIN bucket_deltas bd
                 ON  abc.bucket = bd.bucket
                 AND abc.color  = bd.color
)

/* 7️⃣  final projection for the chart ------------------------------ */
SELECT
    strftime(bucket, '${fmt}') AS time_key,
    color as color,
    standard_sort              AS sort,
    cumulative_count           AS count
  FROM running
 ORDER BY bucket, sort;
`;
};

// Query for detailed tooltip data
function generateTooltipQuery(timePoint, groupBy = 'day') {
  /* ---------- helpers ------------------------------------------- */
  const pad2 = s => s.toString().padStart(2, '0');

  /* ---------- derive unit + “bucket start” timestamp ------------- */
  const unit =
    groupBy === 'month' ? 'month' :
    groupBy === 'week'  ? 'week'  : 'day';

  let boundaryExpr;       // literal TIMESTAMP that marks the bucket start

  if (groupBy === 'month') {
    const [yy, mm] = timePoint.split('-');
    boundaryExpr = `CAST('${yy}-${pad2(mm)}-01 00:00:00' AS TIMESTAMP)`;

  } else if (groupBy === 'week') {
    // ISO Monday of the requested week, 00:00
    const [yy, wStr] = timePoint.split('-W');
    const w = parseInt(wStr, 10);

    boundaryExpr = `
      CAST(
        DATE '${yy}-01-04'                       -- ISO anchor
        + INTERVAL '${(w - 1) * 7}' DAY
        - INTERVAL ((EXTRACT(dow FROM DATE '${yy}-01-04') + 6) % 7) DAY
        AS TIMESTAMP
      )`;

  } else { // 'day'
    boundaryExpr = `CAST('${timePoint} 00:00:00' AS TIMESTAMP)`;
  }

  /* ---------- SQL ------------------------------------------------ */
  return `
WITH
  /* bucket start as TIMESTAMP + its DATE_TRUNC(unit, …) value */
  boundary AS (
    SELECT
      ${boundaryExpr}                           AS ts,
      DATE_TRUNC('${unit}', ${boundaryExpr})    AS bucket
  ),

  /* items that are alive in this bucket --------------------------- */
  status_at_boundary AS (
    SELECT
      sht.subItemId,
      sht.statusId
    FROM   boundary b
    JOIN   filteredStatusHistory sht
           ON  DATE_TRUNC('${unit}', sht.validFrom) <= b.bucket
           AND (
                 sht.validTo IS NULL
                 OR DATE_TRUNC('${unit}', sht.validTo) > b.bucket
               )
  ),

  /* aggregated counts by status title / colour -------------------- */
  counts AS (
    SELECT
      st.title,
      st.color,
      MIN(st.sort)              AS sort,
      COUNT(DISTINCT subItemId) AS count
    FROM   status_at_boundary sab
    JOIN   status_templates st  ON sab.statusId = st.id
    GROUP  BY st.title, st.color
  )

/* final projection ------------------------------------------------ */
SELECT
  title as title,
  color as color,
  sort as sort,
  count as count
FROM counts
ORDER BY sort, title;
`;
}

const fetchTooltipData = async (timePoint) => {
  if (!conn.value) return null;
  
  // Return cached data if available
  if (tooltipCache.value.has(timePoint)) {
    return tooltipCache.value.get(timePoint);
  }
  
  try {
    const tooltipQuery = generateTooltipQuery(timePoint, groupBy.value);
    const data = await executeAndProcessTooltipQuery(tooltipQuery);
    
    // Process and cache the data
    const tooltipData = data.map(item => ({
      title: item.title || 'Unknown',
      color: item.color || '#CCCCCC',
      count: Number(item.count) || 0
    }));
    
    tooltipCache.value.set(timePoint, tooltipData);
    return tooltipData;
  } catch (error) {
    console.error('Error fetching tooltip data:', error);
    return [];
  }
};

// Debounced tooltip data fetcher
// Add this log to see what time point format is being passed to the tooltip
const debouncedFetchTooltipData = (timePoint) => {
  clearTimeout(tooltipDebounceTimer.value);
  return new Promise((resolve) => {
    tooltipDebounceTimer.value = setTimeout(async () => {
      const data = await fetchTooltipData(timePoint);
      resolve(data);
    }, 200);
  });
};

const fetchBurnupData = async () => {
  if (!conn.value) return;
  
  isLoading.value = true;
  
  try {
    const dynamicQuery = generateBurnupQuery(groupBy.value);
    const result = await executeAndProcessQuery(dynamicQuery);
    
    // Process the data and convert BigInt values to numbers
    const data = result.map(row => ({
      timeKey: row.time_key,
      color: row.color,
      count: Number(row.count),
      cumulative_count: Number(row.count),
      sort: Number(row.sort) 
    }));
    
    // Process the flat structure
    const timeKeys = [...new Set(data.map(row => row.timeKey))];
    const colorMap = new Map();
    
    // Find all unique colors and create series objects
    data.forEach(row => {
      if (row.color) {
        if (!colorMap.has(row.color)) {
          colorMap.set(row.color, {
            name: row.color,
            type: 'line',
            stack: 'Total',
            areaStyle: {},
            emphasis: { focus: 'series' },
            data: new Array(timeKeys.length).fill(0),
            itemStyle: { color: row.color },
            lineStyle: { color: row.color },
            sort: row.sort
          });
        }
      }
    });
    
    // Fill in cumulative counts for each time period
    data.forEach(row => {
      if (row.color) {
        const seriesObj = colorMap.get(row.color);
        const timeIndex = timeKeys.indexOf(row.timeKey);
        if (timeIndex !== -1 && seriesObj) {
          seriesObj.data[timeIndex] = row.cumulative_count;
        }
      }
    });
    
    // Add series names using status templates
    colorMap.forEach((series, color) => {
      const matchingStatuses = Object.values(statusTemplatesObj.value)
        .filter(tpl => tpl.color === color)
        .map(tpl => tpl.title);
      
      if (matchingStatuses.length > 0) {
        series.name = matchingStatuses.join(', ');
      }
    });
    
    // Update chart data
    chartData.value = {
      timeData: timeKeys,
      seriesData: Array.from(colorMap.values()).sort((a, b) => b.sort - a.sort),
      rawTimeKeys: timeKeys
    };
    
    tooltipCache.value = new Map();
    updateChartOption();
    
  } catch (error) {
    console.error('Error fetching burnup data:', error);
  } finally {
    isLoading.value = false;
  }
};

const updateChartOption = () => {
  chartOption.value = {
    tooltip: {
      trigger: 'axis',
      confine: true,
      enterable: true,
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      formatter: function(params) {
        const timePoint = params[0].axisValue;
        
        // Update current tooltip time point to trigger data fetching
        currentTooltipTimePoint.value = timePoint;
        
        // Check if we have cached data already
        if (tooltipCache.value.has(timePoint)) {
          const data = tooltipCache.value.get(timePoint);
          return formatTooltipContent(timePoint, data);
        } else {
          // Return loading state
          return `<div style="max-height: 300px; overflow-y: auto;">
                  ${formatAxisLabel(timePoint)}<br/>
                  <div>Loading data...</div>
                  </div>`;
        }
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%', // Increased to make room for zoom slider
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData.value.timeData,
      axisLabel: {
        color: chartColor.value,
        formatter: formatAxisLabel
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: chartColor.value
      }
    },
    dataZoom: [
      {
        type: 'slider',
        show: true,
        xAxisIndex: 0,
        start: zoomStart.value,
        end: zoomEnd.value,
        height: 30,
        bottom: 10,
        borderColor: 'transparent',
        backgroundColor: 'rgba(47, 69, 84, 0.1)',
        dataBackground: {
          lineStyle: {
            color: 'rgba(47, 69, 84, 0.3)'
          },
          areaStyle: {
            color: 'rgba(47, 69, 84, 0.3)'
          }
        },
        fillerColor: 'rgba(167, 183, 204, 0.4)',
        handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '80%',
        handleStyle: {
          color: '#a7b7cc'
        },
        labelFormatter: (value) => {
          const index = Math.floor(value);
          if (index >= 0 && index < chartData.value.timeData.length) {
            return formatAxisLabel(chartData.value.timeData[index]);
          }
          return '';
        }
      },
      {
        type: 'inside',
        xAxisIndex: 0,
        start: zoomStart.value,
        end: zoomEnd.value,
        zoomOnMouseWheel: 'shift',
        moveOnMouseWheel: true,
        moveOnMouseMove: true
      }
    ],
    series: chartData.value.seriesData.map(series => ({
      ...series,
      stack: 'Total',
      stackStrategy: 'all',
      areaStyle: {
        opacity: 0.7
      },
      smooth: false,
      emphasis: {
        focus: 'series',
        blurScope: 'coordinateSystem'
      }
    }))
  };
};

// Reset zoom to show all data
const resetZoom = () => {
  zoomStart.value = 0;
  zoomEnd.value = 100;
  isZoomed.value = false;
  
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.dispatchAction({
      type: 'dataZoom',
      start: 0,
      end: 100
    });
  }
};

// Handle chart click (for zoom tracking)
const handleChartClick = (event) => {
  if (chartRef.value && chartRef.value.chart) {
    const option = chartRef.value.chart.getOption();
    
    if (option.dataZoom && option.dataZoom[0]) {
      const { start, end } = option.dataZoom[0];
      zoomStart.value = start;
      zoomEnd.value = end;
      isZoomed.value = start !== 0 || end !== 100;
    }
  }
};

const formatTooltipContent = (timePoint, data) => {
  let tooltip = `<div style="max-height: 300px; overflow-y: auto;">
                ${formatAxisLabel(timePoint)}<br/>`;
  
  if (data && data.length > 0) {
    let total = 0;
    data.forEach(item => {
      tooltip += `<span style="color:${item.color}">●</span> ${item.title}: ${item.count}<br/>`;
      total += item.count;
    });
    
    tooltip += `<strong>${t('total')}: ${total}</strong>`;
  } else {
    tooltip += 'No data available';
  }
  
  tooltip += '</div>';
  return tooltip;
};

const formatAxisLabel = (value) => {
  if (groupBy.value === 'week') {
    const [year, week] = value.split('-W');
    return `${t('week')} ${week}, ${year}`;
  } else if (groupBy.value === 'month') {
    const [year, month] = value.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  } else {
    return value;
  }
};

const downloadChart = () => {
  if (!chartRef.value) return;
  downloadChartAsPNG(chartRef.value.chart, `${t('rdriveBurndownChart')}.png`);
};

watch(currentTooltipTimePoint, async (timePoint) => {
  if (!timePoint || tooltipCache.value.has(timePoint)) return;
  
  // Fetch data for this time point
  tooltipState.value = {
    loading: true,
    data: null,
    timePoint
  };
  
  try {
    const data = await fetchTooltipData(timePoint);
    tooltipState.value.data = data;
    
    // Force tooltip update by refreshing the chart instance
    if (chartRef.value && chartRef.value.chart) {
      chartRef.value.chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: chartData.value.timeData.indexOf(timePoint)
      });
    }
  } catch (error) {
    console.error('Error loading tooltip data:', error);
  } finally {
    tooltipState.value.loading = false;
  }
}, { immediate: false });

// Track dataZoom changes
watch([zoomStart, zoomEnd], ([newStart, newEnd]) => {
  isZoomed.value = newStart !== 0 || newEnd !== 100;
  
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.setOption({
      dataZoom: [
        { start: newStart, end: newEnd },
        { start: newStart, end: newEnd }
      ]
    });
  }
});

watch(
  [
    () => props.filterStatusHistoryContext.filtersLastUpdated, 
    groupBy
  ],
  async () => {
    // Clear tooltip cache when relevant data changes
    tooltipCache.value = new Map();
    // Reset zoom when data changes
    zoomStart.value = 0;
    zoomEnd.value = 100;
    isZoomed.value = false;
    
    // Show loading state immediately when groupBy changes
    if (isLoading.value === false) {
      await fetchBurnupData();
    }
  },
  { deep: true }
);

watch(() => chartColor.value, () => {
  updateChartOption();
});

watch(() => props.dataLoaded, () => {
  fetchBurnupData();
});

onMounted(() => {
  //if (props.dataLoaded) {
  //  fetchBurnupData();
  //}
  
  // Set up zoom change handler
  if (chartRef.value && chartRef.value.chart) {
    chartRef.value.chart.on('datazoom', (params) => {
      zoomStart.value = params.start;
      zoomEnd.value = params.end;
      isZoomed.value = params.start !== 0 || params.end !== 100;
    });
  }
});
</script>

<style scoped>
.chart {
  height: 400px;
  width: 100%;
}

.chart-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.1);
  z-index: 10;
}

.position-relative {
  position: relative;
}
</style>